import { useState } from 'react';
import type { Locale } from '@iac/contracts';
import type { Dictionary } from '../i18n/dictionary';
import type { Story } from '../lib/story';
import { Badge } from './Badge';
import { DataTable, Td, Tr } from './DataTable';
import { DeleteButton } from './DeleteButton';
import { IconNavFlag, IconNavWallet, IconPencilSquare } from './icons';

interface Wallet {
  name: string;
  currency: string;
  balance: number;
}

interface Goal {
  name: string;
  target: number;
  month: string;
  allocated: number;
  short: number | null;
}

const pl = {
  name: 'Nazwa',
  currency: 'Waluta',
  balance: 'Saldo',
  goal: 'Cel',
  target: 'Kwota',
  month: 'Miesiąc',
  allocated: 'Przydzielono',
  status: 'Status',
  funded: 'sfinansowany',
  short: (amount: string) => `brakuje ${amount}`,
  monthsLeft: '4 miesiące do końca',
  note: 'Notatka',
  saved: 'Zapisano',
  kind: 'Rodzaj',
  wallet: 'Portfel',
  goalKind: 'Cel',
  year: 'Rok',
  empty: 'Brak portfeli.',
  edit: (name: string) => `Edytuj portfel ${name}`,
  delete: (name: string) => `Usuń portfel ${name}`,
  confirm: (name: string) => `Usunąć portfel „${name}”?`,
  wallets: [
    { name: 'Konto osobiste', currency: 'PLN', balance: 4210.5 },
    { name: 'Oszczędności', currency: 'PLN', balance: 18000 },
    { name: 'Wakacje', currency: 'EUR', balance: 640 },
  ] as Wallet[],
  goals: [
    {
      name: 'Samochód',
      target: 40000,
      month: 'marzec 2027',
      allocated: 40000,
      short: null,
    },
    {
      name: 'Remont',
      target: 25000,
      month: 'luty 2027',
      allocated: 18000,
      short: 7000,
    },
  ] as Goal[],
  notes: [
    'Jan mieszka przy ul. Dębowej 12 w Krakowie, trzecie piętro, domofon 34. Kod do bramy zmienia się co kwartał — ostatni dostałem w SMS-ie we wrześniu.',
    'Hasło do Wi-Fi u rodziców jest na lodówce.',
  ],
  months: [
    'sty',
    'lut',
    'mar',
    'kwi',
    'maj',
    'cze',
    'lip',
    'sie',
    'wrz',
    'paź',
    'lis',
    'gru',
  ],
};

const sample: Dictionary<typeof pl> = {
  pl,
  en: {
    name: 'Name',
    currency: 'Currency',
    balance: 'Balance',
    goal: 'Goal',
    target: 'Target',
    month: 'Month',
    allocated: 'Allocated',
    status: 'Status',
    funded: 'funded',
    short: (amount) => `${amount} short`,
    monthsLeft: '4 months to go',
    note: 'Note',
    saved: 'Saved',
    kind: 'Kind',
    wallet: 'Wallet',
    goalKind: 'Goal',
    year: 'Year',
    empty: 'No wallets yet.',
    edit: (name) => `Edit wallet ${name}`,
    delete: (name) => `Delete wallet ${name}`,
    confirm: (name) => `Delete wallet "${name}"?`,
    wallets: [
      { name: 'Current account', currency: 'PLN', balance: 4210.5 },
      { name: 'Savings', currency: 'PLN', balance: 18000 },
      { name: 'Holidays', currency: 'EUR', balance: 640 },
    ],
    goals: [
      {
        name: 'Car',
        target: 40000,
        month: 'March 2027',
        allocated: 40000,
        short: null,
      },
      {
        name: 'Renovation',
        target: 25000,
        month: 'February 2027',
        allocated: 18000,
        short: 7000,
      },
    ],
    notes: [
      'John lives at 12 Oak Street, Leeds, third floor, buzzer 34. The gate code changes every quarter — the latest one came by text in September.',
      "The Wi-Fi password at my parents' is on the fridge.",
    ],
    months: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
  },
};

const money = (n: number) =>
  n.toLocaleString('pl-PL', { minimumFractionDigits: 2 });

function WalletActions({ locale, name }: { locale: Locale; name: string }) {
  const t = sample[locale];
  return (
    <div className="row-actions">
      <button
        type="button"
        className="btn-icon-ghost"
        aria-label={t.edit(name)}
        title={t.edit(name)}
      >
        <IconPencilSquare />
      </button>
      <DeleteButton
        label={t.delete(name)}
        confirm={t.confirm(name)}
        onConfirm={() => {}}
      />
    </div>
  );
}

function Sortable({ locale }: { locale: Locale }) {
  const t = sample[locale];
  const [sort, setSort] = useState<{
    key: 'name' | 'balance';
    dir: 1 | -1;
  }>({ key: 'balance', dir: -1 });
  const toggle = (key: 'name' | 'balance') =>
    setSort((s) => ({ key, dir: s.key === key ? (-s.dir as 1 | -1) : 1 }));
  const rows = [...t.wallets].sort((a, b) =>
    sort.key === 'name'
      ? sort.dir * a.name.localeCompare(b.name, locale)
      : sort.dir * (a.balance - b.balance),
  );
  const direction = (key: 'name' | 'balance') =>
    sort.key !== key ? undefined : sort.dir === 1 ? 'ascending' : 'descending';

  return (
    <DataTable
      columns={[
        {
          label: t.name,
          sort: direction('name'),
          onSort: () => toggle('name'),
        },
        {
          label: t.balance,
          align: 'end',
          sort: direction('balance'),
          onSort: () => toggle('balance'),
        },
      ]}
    >
      {rows.map((row) => (
        <Tr key={row.name}>
          <Td emphasis>{row.name}</Td>
          <Td numeric>{money(row.balance)}</Td>
        </Tr>
      ))}
    </DataTable>
  );
}

export default {
  kind: 'component',
  title: { pl: 'Tabela', en: 'Data table' },
  description: {
    pl: 'Tabela w karcie — jedyny sposób na tabelę w tym projekcie (lint odrzuca gołe `<table>`). Zostaje tabelą na każdej szerokości. Kolumna z `priority: secondary` znika poniżej md: na telefonie wariant „Kolumny drugorzędne” nie pokazuje Miesiąca ani Przydzielono, a „Długi tekst” — daty. Tabela, która mimo to się nie mieści („Za szeroka”), przewija się w bok wewnątrz karty, nie strona. Wiersze są tylko do odczytu; akcje to przyciski z ikonami z `aria-label`, a edycja odbywa się w oknie modalnym. Wyrównanie komórka bierze z kolumny. Biblioteka renderuje się w szerokości, w której ją oglądasz — mobilne zachowanie sprawdzisz na telefonie.',
    en: 'A table in a card — the only way to render a table in this project (lint rejects a raw `<table>`). It stays a table at every width. A column with `priority: secondary` is hidden below md: on a phone the "Secondary columns" variant drops Month and Allocated, and "Long text" drops the date. A table that still does not fit ("Too wide") scrolls sideways inside its card, not the page. Rows are read-only; actions are icon buttons with an `aria-label`, and editing happens in a modal. Each value takes its alignment from its column. The Library renders at the width you view it at — check the mobile behaviour on a phone.',
  },
  variants: [
    {
      name: { pl: 'Podstawowa', en: 'Basic' },
      render: (locale) => {
        const t = sample[locale];
        return (
          <DataTable
            columns={[
              { label: t.name },
              { label: t.currency, align: 'center' },
              { label: t.balance, align: 'end' },
            ]}
          >
            {t.wallets.map((row) => (
              <Tr key={row.name}>
                <Td emphasis>{row.name}</Td>
                <Td>{row.currency}</Td>
                <Td numeric>{money(row.balance)}</Td>
              </Tr>
            ))}
          </DataTable>
        );
      },
    },
    {
      name: { pl: 'Pusta', en: 'Empty' },
      render: (locale) => (
        <DataTable
          columns={[
            { label: sample[locale].name },
            { label: sample[locale].balance, align: 'end' },
          ]}
          empty={sample[locale].empty}
        />
      ),
    },
    {
      name: { pl: 'Kolumny drugorzędne', en: 'Secondary columns' },
      render: (locale) => {
        const t = sample[locale];
        return (
          <DataTable
            columns={[
              { label: t.goal },
              { label: t.target, align: 'center' },
              { label: t.month, align: 'center', priority: 'secondary' },
              { label: t.allocated, align: 'center', priority: 'secondary' },
              { label: t.status, align: 'center' },
            ]}
          >
            {t.goals.map((goal) => (
              <Tr key={goal.name}>
                <Td emphasis>{goal.name}</Td>
                <Td numeric>{money(goal.target)}</Td>
                <Td>{goal.month}</Td>
                <Td numeric>{money(goal.allocated)}</Td>
                <Td>
                  {goal.short ? (
                    <Badge tone="danger">{t.short(money(goal.short))}</Badge>
                  ) : (
                    <Badge tone="success">{t.funded}</Badge>
                  )}
                </Td>
              </Tr>
            ))}
          </DataTable>
        );
      },
    },
    {
      name: { pl: 'Akcje wiersza', en: 'Row actions' },
      render: (locale) => {
        const t = sample[locale];
        return (
          <DataTable
            columns={[
              { label: t.name },
              { label: t.balance, align: 'end' },
              { align: 'center' },
            ]}
          >
            {t.wallets.map((row) => (
              <Tr key={row.name}>
                <Td emphasis>{row.name}</Td>
                <Td numeric>{money(row.balance)}</Td>
                <Td>
                  <WalletActions locale={locale} name={row.name} />
                </Td>
              </Tr>
            ))}
          </DataTable>
        );
      },
    },
    {
      name: { pl: 'Sortowanie', en: 'Sortable' },
      render: (locale) => <Sortable locale={locale} />,
    },
    {
      name: { pl: 'Długi tekst', en: 'Long text' },
      render: (locale) => {
        const t = sample[locale];
        return (
          <DataTable
            columns={[
              { label: t.note },
              { label: t.saved, align: 'center', priority: 'secondary' },
              { align: 'center' },
            ]}
          >
            {t.notes.map((note) => (
              <Tr key={note}>
                <Td>{note}</Td>
                <Td className="text-hint">12.10.2026</Td>
                <Td>
                  <WalletActions locale={locale} name={t.note} />
                </Td>
              </Tr>
            ))}
          </DataTable>
        );
      },
    },
    {
      name: { pl: 'Ikony i etykiety', en: 'Icons and badges' },
      render: (locale) => {
        const t = sample[locale];
        return (
          <DataTable
            columns={[
              { label: t.kind, align: 'center' },
              { label: t.name },
              { label: t.status, align: 'center' },
            ]}
          >
            <Tr>
              <Td>
                <span role="img" aria-label={t.wallet} title={t.wallet}>
                  <IconNavWallet />
                </span>
              </Td>
              <Td emphasis>{t.wallets[1].name}</Td>
              <Td>
                <Badge tone="success">{t.funded}</Badge>
              </Td>
            </Tr>
            <Tr>
              <Td>
                <span role="img" aria-label={t.goalKind} title={t.goalKind}>
                  <IconNavFlag />
                </span>
              </Td>
              <Td emphasis>{t.goals[1].name}</Td>
              <Td>
                <Badge tone="danger">{t.short(money(7000))}</Badge>
                <span className="text-hint">{t.monthsLeft}</span>
              </Td>
            </Tr>
          </DataTable>
        );
      },
    },
    {
      name: { pl: 'Za szeroka', en: 'Too wide' },
      render: (locale) => {
        const t = sample[locale];
        return (
          <DataTable
            columns={[
              { label: t.year },
              ...t.months.map((month) => ({
                label: month,
                align: 'end' as const,
              })),
            ]}
          >
            {[2025, 2026].map((year) => (
              <Tr key={year}>
                <Td emphasis>{year}</Td>
                {t.months.map((month, index) => (
                  <Td key={month} numeric>
                    {money(1200 + index * 85 + (year - 2025) * 310)}
                  </Td>
                ))}
              </Tr>
            ))}
          </DataTable>
        );
      },
    },
  ],
} satisfies Story;
